import React, { useMemo } from 'react';
import { Box, Typography, Avatar, Stack } from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import useInterveneSx from '../../hooks/useInterveneSx';
import useInterveneTheme from '../../hooks/useInterveneTheme';
import { normalizeInterveneMessage } from '../../utils/normalizeInterveneMessage';
import { getConversationAvatarTone } from '../../utils/getConversationAvatarTone';
import { getMessageLayoutProfile } from '../../utils/messageLayoutProfile';
import MessageBody from './messages/MessageBody';
import { MessageBubbleProvider } from './messages/MessageBubbleContext';

function getInitial(name) {
  if (!name) return '?';
  return name.charAt(0).toUpperCase();
}

function formatTime(dateValue) {
  try {
    return new Date(dateValue).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}

function getStatusIcon(statusType, bubbleStyles) {
  switch ((statusType || '').toLowerCase()) {
    case 'sent':
    case 'sent to meta':
    case 'new message sent':
      return <DoneIcon fontSize="inherit" sx={bubbleStyles.statusSent} />;
    case 'delivered':
      return <DoneAllIcon fontSize="inherit" sx={bubbleStyles.statusSent} />;
    case 'read':
      return <DoneAllIcon fontSize="inherit" sx={bubbleStyles.statusRead} />;
    default:
      return null;
  }
}

function getBubbleShapeStyles(
  bubbleStyles,
  { isMine, isFirstInGroup, isLastInGroup, isAttachmentPrimary }
) {
  if (isAttachmentPrimary) {
    return bubbleStyles.bubbleAttachmentShape;
  }

  if (isMine) {
    if (isFirstInGroup && isLastInGroup) return bubbleStyles.bubbleMineSingle;
    if (isFirstInGroup) return bubbleStyles.bubbleMineFirst;
    if (isLastInGroup) return bubbleStyles.bubbleMineLast;
    return bubbleStyles.bubbleMineMiddle;
  }

  if (isFirstInGroup && isLastInGroup) return bubbleStyles.bubbleTheirSingle;
  if (isFirstInGroup) return bubbleStyles.bubbleTheirFirst;
  if (isLastInGroup) return bubbleStyles.bubbleTheirLast;
  return bubbleStyles.bubbleTheirMiddle;
}

function MessageBoxComponent({
  message,
  isMine,
  senderName,
  senderAvatar,
  isFirstInGroup = true,
  isLastInGroup = true,
  isGrouped = false,
}) {
  const bubbleStyles = useInterveneSx().messageBubble;
  const { chat } = useInterveneTheme();
  const avatarTone = getConversationAvatarTone(chat, {});
  const normalized = useMemo(() => normalizeInterveneMessage(message), [message]);
  const layoutProfile = useMemo(() => getMessageLayoutProfile(normalized), [normalized]);
  const timestamp = normalized.timestamp;
  const isAttachmentPrimary = layoutProfile.isAttachmentPrimary;
  const statusIcon = isMine ? getStatusIcon(normalized.status, bubbleStyles) : null;
  const showAvatar = !isMine && isLastInGroup;
  const showSenderName = !isMine && isFirstInGroup && senderName;
  const formattedTime = timestamp ? formatTime(timestamp) : '';
  const showMetaBelow = Boolean(
    formattedTime || statusIcon || (normalized.attribution && isMine)
  );

  const metaOverlay = useMemo(
    () => ({
      show: false,
      timestamp,
      formattedTime,
      statusIcon,
      attribution: null,
    }),
    [formattedTime, statusIcon, timestamp]
  );

  return (
    <Box
      sx={{
        ...bubbleStyles.row,
        ...(isMine ? bubbleStyles.rowMine : bubbleStyles.rowTheir),
        ...(isGrouped && !isFirstInGroup ? bubbleStyles.rowGrouped : null),
      }}
    >
      {!isMine && (
        <Box sx={bubbleStyles.avatarSlot}>
          {showAvatar ? (
            <Avatar
              src={senderAvatar || ''}
              sx={{
                ...bubbleStyles.avatar,
                bgcolor: avatarTone.background,
                color: avatarTone.foreground,
              }}
            >
              {getInitial(senderName)}
            </Avatar>
          ) : (
            <Box sx={bubbleStyles.avatarSpacer} />
          )}
        </Box>
      )}

      <Box
        sx={{
          ...bubbleStyles.contentColumn,
          ...(isMine ? bubbleStyles.contentColumnMine : bubbleStyles.contentColumnTheir),
        }}
      >
        {showSenderName && (
          <Typography variant="caption" sx={bubbleStyles.senderLabel}>
            {senderName}
          </Typography>
        )}

        <Box
          sx={{
            ...bubbleStyles.bubble,
            ...(isAttachmentPrimary
              ? bubbleStyles.bubbleAttachmentNeutral
              : isMine
                ? bubbleStyles.bubbleMine
                : bubbleStyles.bubbleTheir),
            ...getBubbleShapeStyles(bubbleStyles, {
              isMine,
              isFirstInGroup,
              isLastInGroup,
              isAttachmentPrimary,
            }),
          }}
        >
          <MessageBubbleProvider
            isMine={isMine}
            layoutProfile={layoutProfile}
            metaOverlay={metaOverlay}
          >
            <MessageBody message={normalized} />
          </MessageBubbleProvider>
        </Box>

        {showMetaBelow && (
          <Stack
            direction="row"
            spacing={0.375}
            sx={{
              ...bubbleStyles.metaBelowBubble,
              ...(isMine ? bubbleStyles.metaBelowBubbleMine : bubbleStyles.metaBelowBubbleTheir),
            }}
          >
            {normalized.attribution && isMine && (
              <Typography component="span" variant="caption">
                {normalized.attribution}
              </Typography>
            )}
            {formattedTime && (
              <Typography component="span" variant="caption">
                {formattedTime}
              </Typography>
            )}
            {statusIcon && (
              <Box component="span" title={normalized.status || 'sent'} sx={bubbleStyles.statusInline}>
                {statusIcon}
              </Box>
            )}
          </Stack>
        )}
      </Box>
    </Box>
  );
}

function areMessageBoxPropsEqual(prev, next) {
  const prevId = prev.message?.id || prev.message?.msg_id || prev.message?.w_id;
  const nextId = next.message?.id || next.message?.msg_id || next.message?.w_id;
  const prevStatus = prev.message?.status?.type || prev.message?.status;
  const nextStatus = next.message?.status?.type || next.message?.status;

  return (
    prevId === nextId &&
    prevStatus === nextStatus &&
    prev.isMine === next.isMine &&
    prev.senderName === next.senderName &&
    prev.senderAvatar === next.senderAvatar &&
    prev.isFirstInGroup === next.isFirstInGroup &&
    prev.isLastInGroup === next.isLastInGroup &&
    prev.isGrouped === next.isGrouped
  );
}

const MessageBox = React.memo(MessageBoxComponent, areMessageBoxPropsEqual);

export default MessageBox;
